import {
  encodeHealthMilli,
  PVP_RULES_VERSION,
  PVP_TEAM_PROTOCOL_VERSION,
  type PvpAppearance,
  type PvpCombatProfileWire,
  type PvpMatchState,
  type PvpParticipantId,
  type PvpRuneWireKind,
  type PvpSummonFrame,
  type PvpTeam,
  type PvpTeamBlastFrame,
  type PvpTeamBleedBurstFrame,
  type PvpTeamBleedFrame,
  type PvpTeamMissileFrame,
  type PvpTeamParticipantFrame,
  type PvpTeamPoisonFrame,
  type PvpTeamProcFrame,
  type PvpTeamProjectileFrame,
  type PvpTeamReflectFrame,
  type PvpTeamRosterEntry,
  type PvpTeamSelf,
  type PvpTeamStateFrame,
  type PvpTeamWelcomeFrame,
  pvpCopyCombatProfileWire,
} from '@kenocyber/shared';
import type { CardAbilityState } from '../cards/abilities';
import { cardIsCloaked } from '../cards/effects';
import { sawBladeCount } from '../damage/saw';
import {
  PVP_ARENA_HEIGHT_MU,
  PVP_ARENA_WIDTH_MU,
  PVP_MAX_HEALTH_MILLI,
  PVP_OBSTACLES,
  PVP_PROJECTILE_RADIUS_MU,
  PVP_RECONNECT_GRACE_MS,
  PVP_SHIP_RADIUS_MU,
  PVP_TICK_MS,
  PVP_TIME_LIMIT_TICKS,
} from './balance';
import type { PvpBleedingMarkState } from './bleeding';
import {
  pvpCardBlastFields,
  pvpCardFlagFields,
  pvpCardSlotsOf,
  pvpCardVisualFields,
  pvpCardVisualStateOf,
  pvpPublicCardFlags,
  pvpSummonFrameOf,
} from './card-frames';
import type { PvpMissileState } from './missile';
import type { PvpPoisonStackState } from './poison';
import type { PvpParticipantProjectileState, PvpParticipantShipState } from './roster';
import type { PvpRuneState } from './runes';
import { shipHasSuperCannons } from './super-cannons';

/**
 * The team match's frames, built as pure values.
 *
 * ## Three steps, deliberately separated
 *
 * 1. the PUBLIC world - one object, built once per tick, identical for all six;
 * 2. the ENVELOPE - what only the recipient is told, built per socket;
 * 3. sending, which happens in the transport and never here.
 *
 * The room used to serialise its internal state directly, which is fine while
 * there are two ships and one shape. With six chairs and a private `ack` it
 * stops being fine: the temptation is to build one frame and patch the
 * recipient's fields into it, and a patched frame is one shared object that six
 * sockets can see each other's edits through. So the world is built once and
 * frozen by construction - every envelope gets its own top-level object, and
 * the arrays inside it are rebuilt rather than adopted.
 *
 * ## Canonical order, and what it buys
 *
 * Participants come out in team-then-slot order whatever order the roster
 * arrived in, so two rooms with the same future serialise to the same bytes and
 * a client can index by identity rather than by position. `team-wire.test.ts`
 * shuffles the input and compares the output byte for byte.
 *
 * ## What never comes out of here
 *
 * No account id, e-mail, seat, `winnerSeat`, ticket, signature, invite code,
 * launch, cookie, seed, connection id, address, wallet or reward. The builders
 * below take SHIPS and PROJECTILES - values from the simulation, which has
 * never heard of an account - plus a roster of chairs that carries only what a
 * screen has to draw.
 */

/** What a chair looks like to everybody, before and during a match. */
export interface PvpTeamChairPublic {
  readonly participantId: PvpParticipantId;
  readonly team: 'a' | 'b';
  readonly slot: 1 | 2 | 3;
  readonly connected: boolean;
  readonly ready: boolean;
  /** Null until that chair has presented its capability. Never invented. */
  readonly appearance: PvpAppearance | null;
  /** O nickname de quem se sentou, ou `null` (gate V28H.R). Do bilhete. */
  readonly nickname: string | null;
  /**
   * What that hull IS, from the SIMULATION rather than from the chair.
   *
   * Never null and never absent: a chair's ship is spawned when the room builds
   * its simulation, so the room knows this before anybody sits down. An
   * appearance is null until a person presents a capability because it
   * describes their loadout; a profile describes the hull, and the hull is
   * already there.
   */
  readonly combatProfile: PvpCombatProfileWire;
}

/** Everything six clients must agree about, built once per tick. */
export interface PvpTeamWorld {
  readonly tick: number;
  readonly state: PvpMatchState;
  readonly participants: readonly PvpTeamParticipantFrame[];
  readonly projectiles: readonly PvpTeamProjectileFrame[];
  /** Os mísseis em voo (gate V28G.L). Uma lista à parte, como no wire. */
  readonly missiles: readonly PvpTeamMissileFrame[];
  /*
   * O TIPO ESTREITO da runa, e não `string`.
   *
   * Ele era largo, e o `as` no fim de `pvpTeamStateFrameOf` engolia a diferença.
   * Tirado o `as`, o compilador diz o que sempre foi verdade: o que viaja aqui
   * são as três runas que existem, e não um texto qualquer.
   */
  readonly runes: readonly { readonly p: number; readonly k: PvpRuneWireKind | null }[];
  /** Os procs confirmados desde a publicacao anterior, por ordem. */
  readonly procs: readonly PvpTeamProcFrame[];
  /** As explosões confirmadas desde a publicação anterior, por ordem. */
  readonly blasts: readonly PvpTeamBlastFrame[];
  /**
   * Os stacks de veneno ATIVOS agora (gate V28G.M).
   *
   * Estado, e não "o que houve desde a publicação anterior" como `procs` e
   * `blasts` são. Um stack dura seis segundos e tem uma marca desenhada durante
   * esse tempo; um espetador que chegue a meio tem de a ver.
   */
  readonly poison: readonly PvpTeamPoisonFrame[];
  /**
   * As marcas de sangramento ATIVAS agora (gate V28G.N).
   *
   * Estado, como `poison`. Uma marca existe até alguém a mandar rebentar, e um
   * espetador que chegue a meio tem de a ver.
   */
  readonly bleeding: readonly PvpTeamBleedFrame[];
  /**
   * As DETONAÇÕES confirmadas desde a publicação anterior, por ordem.
   *
   * Evento, ao lado do estado acima: as marcas saírem da lista é ambíguo -
   * detonação, morte do alvo, morte do autor - e isto diz qual das três foi.
   */
  readonly bleedBursts: readonly PvpTeamBleedBurstFrame[];
  /**
   * As REFLEXÕES confirmadas desde a publicação anterior, por ordem (V28G.O).
   *
   * Evento, como `procs` e `blasts`: uma reflexão acontece e acaba. Carrega as
   * DUAS posições - o refletor e o destinatário - porque o brilho corre numa e
   * o feixe acaba na outra, e num frame que junta dois tiques os dois cascos já
   * se moveram.
   */
  readonly reflects: readonly PvpTeamReflectFrame[];
  /**
   * **As invocações a existir** (gates V28H.L e V28H.S).
   *
   * Ela era escrita por `pvpTeamWorldOf` e não estava DECLARADA aqui, portanto
   * `pvpTeamStateFrameOf` não tinha como a ver - e não a copiava. O drone do 3x3
   * existia na sala, era publicado no mundo e morria entre o mundo e o frame.
   *
   * Opcional porque a esmagadora maioria dos tiques não tem nenhuma.
   */
  readonly sm?: readonly PvpSummonFrame[];
  readonly left: number;
}

function teamLetterOf(value: string): 'a' | 'b' {
  if (value !== 'a' && value !== 'b') {
    // Unreachable: a roster is built from the canonical seats. Asserted so a
    // widened team letter fails here rather than reaching a client.
    throw new RangeError(`not a pvp team: ${value}`);
  }
  return value;
}

function slotOf(value: number): 1 | 2 | 3 {
  if (value !== 1 && value !== 2 && value !== 3) {
    throw new RangeError(`not a pvp slot: ${value}`);
  }
  return value;
}

/**
 * One ship as the wire describes it.
 *
 * Every nested object is rebuilt. `effects` is a live object
 * objects inside the simulation, and handing them out would let a client's
 * frame and the running match share a reference - which is the same class of
 * mistake the v2 snapshot made and v3 fixed.
 */
function participantFrameOf(
  ship: PvpParticipantShipState,
  appearance: PvpAppearance | null,
): PvpTeamParticipantFrame {
  return {
    p: ship.participantId,
    tm: teamLetterOf(ship.team),
    sl: slotOf(ship.slot),
    x: ship.x,
    y: ship.y,
    mx: ship.moveX,
    my: ship.moveY,
    ax: ship.aimX,
    ay: ship.aimY,
    hp: encodeHealthMilli(ship.healthMilli),
    alive: ship.healthMilli > 0n,
    c: ship.connected,
    ap: appearance === null ? null : { ...appearance },
    e: {
      dd: ship.effects.doubleDamageTicks,
      sp: ship.effects.speedTicks,
      tp: ship.effects.teleportCharges,
    },
    /* Ausente quando ninguém o empurra, que é o estado normal (gate V28G.J). */
    ...(ship.knockback === null ? {} : { kb: ship.knockback.ticks }),
    /* As serras, ausentes em todo o casco sem a joia (gate V28G.P). */
    ...pvpSawFrameFields(ship),
    /* Os dois canhões temporários, vistos por todos (gate V28H.J). */
    ...(shipHasSuperCannons(ship) ? { sc: 1 as const } : {}),
    /*
     * E os factos públicos das cartas de temporizador (gates V28H.L e V28H.W).
     *
     * A LEITURA sai da mesma função que o duelo usa - `pvpPublicCardFlags` - e
     * já não das seis expressões escritas à mão que aqui estavam. É o que impede
     * os dois protocolos de divergirem: o 3x3 já perdeu `cv`, `cg`, `cw`, as
     * invocações e a bandeira da Bomba por serem escritos duas vezes, e o gate
     * V28H.X quase repetiu o mesmo com dois estados novos.
     */
    ...pvpCardFlagFields(pvpPublicCardFlags(ship.cardEffects)),
    /* E o que as cartas dele estão a desenhar. */
    /*
     * E o que as cartas dele estão a desenhar, da leitura PARTILHADA.
     *
     * Esta decisão esteve escrita duas vezes - aqui e no duelo - e as duas
     * cópias eram idênticas por acidente. Agora há uma, e os dois protocolos
     * dizem exactamente a mesma coisa por construção.
     */
    ...pvpCardVisualFields(pvpCardVisualStateOf(ship.cards, ship.cardEffects)),
  };
}

/**
 * **Os dois campos das serras, ou nenhum** (gate V28G.P).
 *
 * Uma função e não duas expressões repetidas nos dois protocolos: os dois frames
 * dizem a mesma coisa com o mesmo vocabulário, e duas cópias seriam duas
 * oportunidades de um deles passar a omitir a fase de um casco morto.
 *
 * ## O que a AUSÊNCIA significa, e porquê três casos
 *
 * Nenhuma serra. Um casco sem a joia, um casco morto e um casco cujo nível não
 * chega a uma lâmina dizem a mesma coisa ao desenho - não desenhes nada - e
 * dizê-la de três maneiras diferentes daria à cena três estados para tratar.
 *
 * **Um destroço não tem serras**, e é aqui que essa metade fica visível: a
 * simulação já parou o dano no instante da morte, e isto para o desenho no
 * mesmo instante. Sem esta linha, quatro lâminas continuariam a girar à volta de
 * uma nave abatida.
 */
export function pvpSawFrameFields(ship: PvpParticipantShipState): {
  readonly sw?: number;
  readonly sf?: number;
} {
  if (ship.healthMilli <= 0n) {
    return {};
  }
  const blades = sawBladeCount(ship.profile.jewels);
  if (blades <= 0) {
    return {};
  }
  return { sw: blades, sf: ship.sawPhase };
}

/**
 * The public world, in canonical order.
 *
 * `ships` arrives already canonical from `PvpShipRoster`, and the appearance is
 * looked up by IDENTITY rather than by position - so a roster handed over
 * shuffled produces the same bytes.
 */
export function pvpTeamWorldOf(input: {
  readonly tick: number;
  readonly state: PvpMatchState;
  readonly ships: readonly PvpParticipantShipState[];
  readonly projectiles: readonly PvpParticipantProjectileState[];
  readonly missiles: readonly PvpMissileState[];
  readonly runes: readonly PvpRuneState[];
  readonly appearance: ReadonlyMap<PvpParticipantId, PvpAppearance | null>;
  readonly procs: readonly PvpTeamProcFrame[];
  readonly blasts: readonly PvpTeamBlastFrame[];
  readonly poison: readonly PvpPoisonStackState[];
  readonly bleeding: readonly PvpBleedingMarkState[];
  readonly bleedBursts: readonly {
    readonly p: PvpParticipantId;
    readonly t: number;
    readonly q: readonly {
      readonly s: PvpParticipantId;
      readonly n: number;
      /** O dano aplicado, em MILLI. Codificado ao entrar no quadro. */
      readonly d: bigint;
    }[];
  }[];
  readonly reflects: readonly PvpTeamReflectFrame[];
  /**
   * A equipa de quem vai receber este mundo, ou `null` para não esconder nada.
   *
   * `null` é o que uma prova ou uma persistência usa quando quer o mundo
   * inteiro; uma publicação passa sempre uma equipa, porque é ela que decide
   * quem está invisível para quem.
   */
  readonly viewer: PvpTeam | null;
  /** As invocações a existir (gate V28H.L). Ausentes = nenhuma. */
  readonly summons?: readonly {
    readonly id: number;
    readonly kind: string;
    readonly ownerParticipantId: PvpParticipantId;
    readonly x: number;
    readonly y: number;
    readonly aimX: number;
    readonly aimY: number;
    readonly healthMilli: bigint;
    readonly maxHealthMilli: bigint;
    readonly pilotParticipantId: PvpParticipantId | null;
  }[];
}): PvpTeamWorld {
  return {
    tick: input.tick,
    state: input.state,
    /*
     * **Os cascos que ESTE espectador pode ver** (gate V28H.L).
     *
     * Um casco com o Manto Espectral é OMITIDO do mundo de quem não é da equipa
     * dele, e não marcado nele: marcá-lo seria mandar a posição de uma nave
     * invisível para o cliente de quem não a devia ver.
     *
     * Por isso este mundo passou a ser construído por EQUIPA - dois, e não seis:
     * o que decide é a equipa de quem vê. Os aliados continuam a ver o seu.
     */
    participants: input.ships
      .filter(
        (ship) =>
          input.viewer === null || ship.team === input.viewer || !cardIsCloaked(ship.cardEffects),
      )
      .map((ship) => participantFrameOf(ship, input.appearance.get(ship.participantId) ?? null)),
    projectiles: input.projectiles.map((projectile) => ({
      i: projectile.id,
      x: projectile.x,
      y: projectile.y,
      p: projectile.ownerParticipantId,
      tm: teamLetterOf(projectile.ownerTeam),
      /* Ausente num tiro comum, que é quase todo (gate V28G.J). */
      ...(projectile.atomicBomb === null ? {} : { b: true as const }),
      /* E a bomba de uma carta, com o raio que vai abrir (gate V28H.U). */
      /* E a salva de uma ESTRUTURA diz de qual (gate V28H.X). */
      ...(projectile.fromSummon === undefined
        ? {}
        : { sv: projectile.fromSummon === 'leviathan' ? ('l' as const) : ('d' as const) }),
      ...pvpCardBlastFields(projectile.cardBlast),
    })),
    /*
     * Os mísseis, com a sua DIREÇÃO (gate V28G.L).
     *
     * Reembrulhados como tudo o resto - seis frames não partilham um objeto - e
     * carregando `dx`/`dy` porque um míssil curva: medir o rumo entre dois
     * quadros interpolados poria o bico ao lado do sítio para onde ele vai.
     */
    missiles: input.missiles.map((missile) => ({
      i: missile.id,
      x: missile.x,
      y: missile.y,
      dx: missile.dirX,
      dy: missile.dirY,
      p: missile.ownerParticipantId,
      tm: teamLetterOf(missile.ownerTeam),
    })),
    /* As invocações, reembrulhadas como tudo o resto (gate V28H.L). */
    ...(input.summons === undefined || input.summons.length === 0
      ? {}
      : { sm: input.summons.map((summon) => pvpSummonFrameOf(summon)) }),
    runes: input.runes.map((rune) => ({ p: rune.pad, k: rune.kind })),
    /* Reembrulhados como tudo o resto: seis frames nao partilham um objeto. */
    procs: input.procs.map((proc) => ({ ...proc })),
    /* Reembrulhadas pela mesma razão. */
    blasts: input.blasts.map((blast) => ({ ...blast, q: [...blast.q] })),
    /*
     * Os stacks, reduzidos aos três campos que uma cena precisa (gate V28G.M).
     *
     * A autoria, o total congelado e o instante da aplicação NÃO viajam: são
     * mecânica - decidem o crédito da morte e quanto cada parcela cobra - e
     * nenhuma cena os lê. O que viaja é o que existe, sobre quem, e quantas
     * parcelas já pagou, que é o que faz a marca pulsar.
     */
    poison: input.poison.map((stack) => ({
      i: stack.id,
      q: stack.targetParticipantId,
      n: stack.ticksDone,
    })),
    /*
     * As marcas, reduzidas aos três campos que uma cena precisa (V28G.N).
     *
     * A magnitude congelada e o instante da aplicação NÃO viajam: são mecânica, e
     * nenhuma cena os lê. A AUTORIA viaja, ao contrário da de um stack, porque num
     * 3x3 ela é o que separa as marcas que um jogador pode rebentar das de um
     * aliado - e é o que o botão dele conta.
     */
    bleeding: input.bleeding.map((mark) => ({
      i: mark.id,
      p: mark.attackerParticipantId,
      q: mark.targetParticipantId,
    })),
    /* E as detonações, reembrulhadas como tudo o resto. */
    bleedBursts: input.bleedBursts.map((burst) => ({
      p: burst.p,
      t: burst.t,
      q: burst.q.map((target) => ({
        s: target.s,
        n: target.n,
        d: encodeHealthMilli(target.d),
      })),
    })),
    /* E as reflexões, reembrulhadas como tudo o resto. */
    reflects: input.reflects.map((reflect) => ({ ...reflect })),
    left: Math.max(0, (PVP_TIME_LIMIT_TICKS - input.tick) * PVP_TICK_MS),
  };
}

/** The recipient's own half: identity, generation and applied sequence. */
export function pvpTeamSelfOf(chair: {
  readonly participantId: PvpParticipantId;
  readonly team: string;
  readonly slot: number;
  readonly generation: number;
  readonly appliedSeq: number;
  /**
   * Os três espaços de cartas DESTA cadeira (gate V28H.J).
   *
   * Na metade privada porque um arrefecimento é informação de decisão: que os
   * canhões estão montados é público - eles estão no ecrã - mas quanto falta
   * para poderem voltar é coisa que o adversário não tem como ver.
   *
   * Vazio por omissão, e é o estado exato de uma cadeira sem cartas.
   */
  readonly cards?: readonly CardAbilityState[];
}): PvpTeamSelf {
  return {
    p: chair.participantId,
    tm: teamLetterOf(chair.team),
    sl: slotOf(chair.slot),
    gen: chair.generation,
    ack: chair.appliedSeq,
    ck: pvpCardSlotsOf(chair.cards ?? []),
  };
}

export function pvpTeamRosterOf(
  chairs: readonly PvpTeamChairPublic[],
): readonly PvpTeamRosterEntry[] {
  return chairs.map((chair) => ({
    p: chair.participantId,
    tm: chair.team,
    sl: chair.slot,
    c: chair.connected,
    r: chair.ready,
    ap: chair.appearance === null ? null : { ...chair.appearance },
    /* E o nome dele, ao lado da aparência e pela mesma razão (gate V28H.R). */
    n: chair.nickname,
    // Rebuilt, never adopted, like every other nested object here: six frames
    // must not share one object a later edit could travel through.
    cp: pvpCopyCombatProfileWire(chair.combatProfile),
  }));
}

/**
 * A welcome for one recipient.
 *
 * The arena, the radii and the limits are the same constants the room enforces,
 * so a client draws exactly the bounds it is being judged against. The roster
 * is the public one; the only thing that differs between six welcomes is `you`.
 */
export function pvpTeamWelcomeOf(input: {
  readonly matchId: string;
  readonly mode: string;
  readonly state: PvpMatchState;
  readonly self: PvpTeamSelf;
  readonly roster: readonly PvpTeamRosterEntry[];
}): PvpTeamWelcomeFrame {
  return {
    t: 'welcome',
    v: PVP_TEAM_PROTOCOL_VERSION,
    ruleset: 'team_3v3',
    matchId: input.matchId,
    you: { ...input.self },
    state: input.state,
    mode: input.mode,
    rulesVersion: PVP_RULES_VERSION,
    tickMs: PVP_TICK_MS,
    arena: {
      w: PVP_ARENA_WIDTH_MU,
      h: PVP_ARENA_HEIGHT_MU,
      obstacles: PVP_OBSTACLES.map((obstacle) => ({
        x: obstacle.x,
        y: obstacle.y,
        w: obstacle.w,
        h: obstacle.h,
      })),
    },
    roster: input.roster.map((entry) => ({ ...entry })),
    shipRadius: PVP_SHIP_RADIUS_MU,
    projectileRadius: PVP_PROJECTILE_RADIUS_MU,
    maxHealthMilli: Number(PVP_MAX_HEALTH_MILLI),
    timeLimitMs: PVP_TIME_LIMIT_TICKS * PVP_TICK_MS,
    reconnectGraceMs: PVP_RECONNECT_GRACE_MS,
  };
}

/**
 * One state frame: the shared world plus the recipient's own half.
 *
 * The world's arrays are re-wrapped rather than adopted, so six frames built
 * from one world share nothing a later edit could travel through.
 */
export function pvpTeamStateFrameOf(world: PvpTeamWorld, self: PvpTeamSelf): PvpTeamStateFrame {
  return {
    t: 'state',
    v: PVP_TEAM_PROTOCOL_VERSION,
    k: world.tick,
    state: world.state,
    you: { ...self },
    parts: world.participants.map((participant) => ({ ...participant })),
    proj: world.projectiles.map((projectile) => ({ ...projectile })),
    mis: world.missiles.map((missile) => ({ ...missile })),
    runes: world.runes.map((rune) => ({ p: rune.p, k: rune.k })),
    procs: world.procs.map((proc) => ({ ...proc })),
    blasts: world.blasts.map((blast) => ({ ...blast, q: [...blast.q] })),
    psn: world.poison.map((stack) => ({ ...stack })),
    bld: world.bleeding.map((mark) => ({ ...mark })),
    bdt: world.bleedBursts.map((burst) => ({
      ...burst,
      q: burst.q.map((target) => ({ ...target })),
    })),
    rfl: world.reflects.map((reflect) => ({ ...reflect })),
    /*
     * **E as INVOCAÇÕES** (gate V28H.S), reembrulhadas como tudo o resto.
     *
     * Faltavam. O `as PvpTeamStateFrame` que estava no fim desta função era o
     * que o escondia: um objecto afirmado em vez de verificado não é comparado
     * com o tipo, portanto o compilador nunca teve como dizer que faltava um
     * campo. O `as` saiu com esta correcção, e é ele que impede a próxima.
     */
    ...(world.sm === undefined ? {} : { sm: world.sm.map((summon) => ({ ...summon })) }),
    left: world.left,
  };
}
